/**
 * Collection grid interactivity: card photo carousel, on-card metal swatches,
 * the smart buy button (add / more-options / stack), quick view, and the
 * stack modal.
 *
 * Everything survives the theme's filter engine, which re-renders the grid by
 * replacing [data-partial] innerHTML: the carousel is an autonomous custom
 * element (recreated nodes upgrade themselves) and every click handler is
 * delegated on the document, so nothing needs re-binding.
 *
 * Product data for the modals comes from Shopify's public
 * `/products/<handle>.js` JSON; per-card data (metal->variant map, stack
 * partners, pair-photo URLs) ships in a small JSON block inside each card.
 * Add-to-bag POSTs to /cart/add.js, which UpCart intercepts to open its
 * drawer. Stiletto's own quick-view bus is closed over inside theme.js and
 * renders the old product design, so it is deliberately not used.
 */
(function () {
  'use strict';

  /* ---------- card carousel ---------- */

  if (!customElements.get('lc-product-card')) {
    customElements.define('lc-product-card', class extends HTMLElement {
      connectedCallback() {
        if (this._lcInit) return;
        this._lcInit = true;
        var track = this.querySelector('[data-track]');
        if (!track) return;
        var dots = Array.prototype.slice.call(this.querySelectorAll('.lc-card__dots i'));
        var ticking = false;

        track.addEventListener('scroll', function () {
          if (ticking || !dots.length) return;
          ticking = true;
          requestAnimationFrame(function () {
            var i = Math.round(track.scrollLeft / track.clientWidth);
            dots.forEach(function (d, k) { d.classList.toggle('on', k === i); });
            ticking = false;
          });
        }, { passive: true });
      }
    });
  }

  /* ---------- shared helpers ---------- */

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function sized(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function cardData(card) {
    var el = card.querySelector('[data-card-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function cardMetal(card) {
    var active = card.querySelector('.lc-swatch.active');
    return active ? active.dataset.value : null;
  }

  /* The variant the card's current swatch selection points at. */
  function cardVariant(card) {
    var data = cardData(card);
    if (!data) return null;
    var metal = cardMetal(card) || data.default;
    if (metal && data.metals && data.metals[metal]) return data.metals[metal];
    return { id: data.id, av: true };
  }

  function cartAdd(ids, onDone, onFail) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: ids.map(function (id) { return { id: id, quantity: 1 }; }) })
    }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      /* UpCart intercepts /cart/add.js and opens its drawer itself. */
      onDone();
    }).catch(onFail);
  }

  /* Picks the partner variant matching the shopper's metal; must be available.
     Falls back to first available, then first. */
  function matchPartnerVariant(p, metal) {
    var pick = null;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (!pick && v.available) pick = v;
      if (metal && v.available) {
        var hit = [v.option1, v.option2, v.option3].some(function (o) {
          return o && o.toLowerCase() === metal.toLowerCase();
        });
        if (hit) return v;
      }
    }
    return pick || p.variants[0];
  }

  /* ---------- modal shell (shared by quick view and stack) ---------- */

  var modal = null;
  var lastFocus = null;
  var state = null;

  function buildModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'lc-qv';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="lc-qv__wash" data-qv-close></div>' +
      '<div class="lc-qv__panel" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="lc-qv__x" data-qv-close aria-label="Close">&times;</button>' +
        '<div class="lc-qv__body" data-qv-body>' +
          '<div class="lc-qv__spin"></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('on')) close();
    });
    return modal;
  }

  function openShell(kind) {
    buildModal();
    lastFocus = document.activeElement;
    modal.classList.add('on');
    modal.classList.toggle('lc-qv--stack', kind === 'stack');
    modal.classList.toggle('lc-qv--page', kind === 'page');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lc-qv-lock');
    var body = modal.querySelector('[data-qv-body]');
    body.innerHTML = '<div class="lc-qv__spin"></div>';
    modal.querySelector('.lc-qv__x').focus();
    return body;
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('on');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lc-qv-lock');
    state = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- quick view: the real product page in a modal ---------- */

  /* Shopify storefronts send X-Frame-Options: DENY, so the page cannot be
     iframed even same-origin. Instead: fetch the product page's HTML, lift
     the funnel section out of it, and adopt it into the modal along with its
     stylesheet and script (a freshly appended script element re-executes, and
     the funnel JS scopes itself to the .lc-funnel root it finds). */
  function openPageModal(url) {
    var body = openShell('page');
    var pageUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'view=funnel';
    fetch(pageUrl)
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        if (!modal.classList.contains('on')) return; /* closed while loading */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sec = doc.querySelector('.lc-funnel-section') || doc.querySelector('.lc-funnel');
        if (!sec) { window.location.href = url; return; }

        /* Grab asset references BEFORE adopting the section — the section's
           own inert script/link tags travel with it out of the parsed doc. */
        var cssLink = doc.querySelector('link[href*="lc-funnel.css"]');
        var srcEl = doc.querySelector('script[src*="lc-funnel.js"]');
        var cssHref = cssLink ? cssLink.href : null;
        var jsSrc = srcEl ? srcEl.src : null;

        if (cssHref && !document.querySelector('link[href*="lc-funnel.css"]')) {
          var l = document.createElement('link');
          l.rel = 'stylesheet';
          l.href = cssHref;
          document.head.appendChild(l);
        }

        body.innerHTML = '';
        body.appendChild(document.adoptNode(sec));

        if (jsSrc) {
          /* A parser-inserted script never executes; a created one always does. */
          var sc = document.createElement('script');
          sc.src = jsSrc;
          document.body.appendChild(sc);
        }
      })
      .catch(function () { window.location.href = url; });
  }

  /* ---------- option picker (More Options) ---------- */

  function openQuickView(url, fullUrl, preValue) {
    openShell('qv');
    fetch(url + '.js')
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (p) { renderQuickView(p, fullUrl || url, preValue); })
      .catch(function () {
        /* The .js endpoint failing is rare (hidden product, network) — fall
           through to the full product page rather than dead-ending. */
        window.location.href = fullUrl || url;
      });
  }

  function realOptions(p) {
    if (p.variants.length === 1 && p.variants[0].title === 'Default Title') return [];
    return p.options.filter(function (o) { return o.values.length > 0; });
  }

  function findVariant(p, selections) {
    var match = null;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      var ok = selections.every(function (val, k) {
        return val === undefined || v['option' + (k + 1)] === val;
      });
      if (ok) {
        if (v.available) return v;
        if (!match) match = v;
      }
    }
    return match;
  }

  function renderQuickView(p, fullUrl, preValue) {
    var opts = realOptions(p);
    var selections = [];
    var first = null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) { first = p.variants[i]; break; }
    }
    if (!first) first = p.variants[0];
    opts.forEach(function (o, k) { selections[k] = first['option' + (k + 1)]; });

    /* Honour the metal picked on the card, when that combination exists. */
    if (preValue) {
      opts.forEach(function (o, k) {
        if (o.values.indexOf(preValue) > -1) {
          var want = selections.slice();
          want[k] = preValue;
          var v = findVariant(p, want);
          if (v) {
            selections = opts.map(function (_, j) { return v['option' + (j + 1)]; });
            first = v;
          }
        }
      });
    }

    state = { kind: 'qv', product: p, selections: selections, variant: first, fullUrl: fullUrl };

    var imgs = p.images.slice(0, 8);
    var slides = imgs.map(function (src, i) {
      return '<div class="lc-qv__slide"><img src="' + esc(sized(src, 720)) + '" alt="" loading="' + (i === 0 ? 'eager' : 'lazy') + '"></div>';
    }).join('');
    var dots = imgs.length > 1
      ? '<div class="lc-qv__dots" aria-hidden="true">' + imgs.map(function (_, i) { return '<i' + (i === 0 ? ' class="on"' : '') + '></i>'; }).join('') + '</div>'
      : '';
    var navs = imgs.length > 1
      ? '<button type="button" class="lc-qv__nav lc-qv__nav--prev" data-qv-nav="-1" aria-label="Previous photo">&#8249;</button>' +
        '<button type="button" class="lc-qv__nav lc-qv__nav--next" data-qv-nav="1" aria-label="Next photo">&#8250;</button>'
      : '';

    var optRows = opts.map(function (o, k) {
      var pills = o.values.map(function (val) {
        return '<button type="button" class="lc-qv__pill' + (selections[k] === val ? ' active' : '') + '" data-qv-opt="' + k + '" data-value="' + esc(val) + '">' + esc(val) + '</button>';
      }).join('');
      return '<div class="lc-qv__optrow"><span class="lc-qv__optname">' + esc(o.name) + ': <b data-qv-picked="' + k + '">' + esc(selections[k]) + '</b></span><div class="lc-qv__pills">' + pills + '</div></div>';
    }).join('');

    var body = modal.querySelector('[data-qv-body]');
    body.innerHTML =
      '<div class="lc-qv__gallery">' +
        '<div class="lc-qv__track" data-qv-track>' + slides + '</div>' + navs + dots +
      '</div>' +
      '<div class="lc-qv__info">' +
        '<h3 class="lc-qv__title">' + esc(p.title) + '</h3>' +
        '<div class="lc-qv__price" data-qv-price></div>' +
        optRows +
        '<button type="button" class="lc-qv__atc" data-qv-atc></button>' +
        '<a class="lc-qv__details" href="' + esc(fullUrl) + '">View full details &rsaquo;</a>' +
      '</div>';

    syncBuyArea();
    bindTrackDots(body.querySelector('[data-qv-track]'), body.querySelectorAll('.lc-qv__dots i'));
  }

  function bindTrackDots(track, dotList) {
    if (!track) return;
    var dots = Array.prototype.slice.call(dotList || []);
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking || !dots.length) return;
      ticking = true;
      requestAnimationFrame(function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach(function (d, k) { d.classList.toggle('on', k === i); });
        ticking = false;
      });
    }, { passive: true });
  }

  function syncBuyArea() {
    var p = state.product;
    var v = findVariant(p, state.selections) || p.variants[0];
    state.variant = v;

    var priceEl = modal.querySelector('[data-qv-price]');
    if (priceEl) {
      var html = '<span class="lc-qv__now">' + money(v.price) + '</span>';
      if (v.compare_at_price > v.price) {
        html += '<s class="lc-qv__was">' + money(v.compare_at_price) + '</s>' +
                '<span class="lc-qv__save">Save ' + money(v.compare_at_price - v.price) + '</span>';
      }
      priceEl.innerHTML = html;
    }

    var atc = modal.querySelector('[data-qv-atc]');
    if (atc) {
      atc.disabled = !v.available;
      atc.textContent = v.available ? 'Add to Bag — ' + money(v.price) : 'Sold Out';
    }

    if (v.featured_image && v.featured_image.src) {
      var key = v.featured_image.src.split('?')[0].replace(/^\/\//, 'https://');
      var track = modal.querySelector('[data-qv-track]');
      if (track) {
        var slides = track.querySelectorAll('img');
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].src.split('?')[0] === key) {
            track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
            break;
          }
        }
      }
    }
  }

  /* ---------- stack modal ---------- */

  function openStack(card) {
    var data = cardData(card);
    var btn = card.querySelector('[data-card-action]');
    if (!data || !data.partners.length) return;

    var base = cardVariant(card);
    var metal = cardMetal(card) || data.default;
    openShell('stack');

    var fetches = [fetch(btn.dataset.url + '.js').then(function (r) { return r.json(); })]
      .concat(data.partners.map(function (pt) {
        return fetch('/products/' + pt.handle + '.js').then(function (r) { return r.json(); });
      }));

    Promise.all(fetches).then(function (all) {
      renderStack(all[0], all.slice(1), data, base, metal, btn.dataset.fullUrl);
    }).catch(function () {
      window.location.href = btn.dataset.fullUrl;
    });
  }

  /* One tier per row, pick exactly ONE — mirroring the funnel page's bundle
     box: "this piece only", or this piece + one styled partner as a 2-piece
     set at 20% off both. The 20% is honored at checkout by the automatic
     discount, not by this code. */
  function renderStack(baseP, partnerPs, data, baseV, metal, fullUrl) {
    var basePrice = baseV.p != null ? baseV.p : baseP.price;
    var baseImg = sized(baseP.images[0], 900);

    var tiers = [{
      vids: [baseV.id],
      label: baseP.title,
      sub: 'This piece only',
      now: basePrice,
      was: 0,
      hero: baseImg
    }];
    partnerPs.forEach(function (p, i) {
      var v = matchPartnerVariant(p, metal);
      var raw = basePrice + v.price;
      tiers.push({
        vids: [baseV.id, v.id],
        label: '+ ' + p.title,
        sub: '2-piece set — 20% off both',
        now: Math.round(raw * 0.8),
        was: raw,
        thumb: sized((v.featured_image && v.featured_image.src) || p.images[0], 240),
        hero: (data.partners[i] && data.partners[i].pair) || sized(p.images[0], 900),
        available: v.available !== false
      });
    });

    /* Default to the piece itself; the sets are the upsell. */
    var def = 0;
    state = { kind: 'stack', tiers: tiers, picked: def, fullUrl: fullUrl };

    var rows = tiers.map(function (t, i) {
      return '<label class="lc-stack__tier' + (i === def ? ' active' : '') + (t.available === false ? ' soldout' : '') + '">' +
        '<input type="radio" name="lcstacktier" data-stack-tier data-i="' + i + '"' +
          (i === def ? ' checked' : '') + (t.available === false ? ' disabled' : '') + '>' +
        (t.thumb ? '<img src="' + esc(t.thumb) + '" alt="" loading="lazy">' : '') +
        '<span class="lc-stack__name">' + esc(t.label) + '<em>' + esc(t.sub) + '</em></span>' +
        '<span class="lc-stack__price">' + money(t.now) +
          (t.was ? '<s>' + money(t.was) + '</s><b>Save ' + money(t.was - t.now) + '</b>' : '') +
        '</span>' +
      '</label>';
    }).join('');

    var body = modal.querySelector('[data-qv-body]');
    body.innerHTML =
      '<div class="lc-stack">' +
        '<div class="lc-stack__hero"><img src="' + esc(tiers[def].hero) + '" alt="" data-stack-hero></div>' +
        '<div class="lc-stack__info">' +
          '<h3 class="lc-qv__title">Complete the Stack</h3>' +
          '<p class="lc-stack__sub">' + esc(baseP.title) + ' pairs perfectly with these pieces.</p>' +
          rows +
          '<button type="button" class="lc-qv__atc" data-stack-atc></button>' +
          '<a class="lc-qv__details" href="' + esc(fullUrl) + '">View full details &rsaquo;</a>' +
        '</div>' +
      '</div>';

    syncStackTier();
  }

  function syncStackTier() {
    var checked = modal.querySelector('[data-stack-tier]:checked');
    if (!checked) return;
    var i = parseInt(checked.dataset.i, 10);
    state.picked = i;
    var t = state.tiers[i];

    modal.querySelectorAll('.lc-stack__tier').forEach(function (row, k) {
      row.classList.toggle('active', k === i);
    });

    var hero = modal.querySelector('[data-stack-hero]');
    if (hero && t.hero) hero.src = t.hero;

    var atc = modal.querySelector('[data-stack-atc]');
    if (atc) {
      atc.disabled = t.available === false;
      atc.textContent = t.vids.length > 1
        ? 'Add Both to Bag — ' + money(t.now)
        : 'Add to Bag — ' + money(t.now);
    }
  }

  /* ---------- card swatches ---------- */

  function pickSwatch(sw) {
    var card = sw.closest('lc-product-card');
    var data = cardData(card);
    if (!data) return;
    card.querySelectorAll('.lc-swatch').forEach(function (b) { b.classList.remove('active'); });
    sw.classList.add('active');

    var m = data.metals[sw.dataset.value];
    if (!m) return;

    var priceEl = card.querySelector('[data-card-price]');
    if (priceEl && m.p != null) {
      var html = money(m.p);
      if (m.c > m.p) html += '<s class="lc-card__price-was">' + money(m.c) + '</s>';
      priceEl.innerHTML = html;
    }

    /* Swap the lead photo to the variant's own, gorjana-style. */
    if (m.img) {
      var img = card.querySelector('.lc-card__slide img');
      var track = card.querySelector('[data-track]');
      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = m.img;
      }
      if (track) track.scrollTo({ left: 0 });
    }

    var btn = card.querySelector('[data-card-action]');
    if (btn && btn.dataset.cardAction === 'add') {
      btn.disabled = !m.av;
      btn.textContent = m.av ? 'Add to Bag' : 'Sold Out';
    }
  }

  /* ---------- delegated clicks ---------- */

  document.addEventListener('click', function (e) {
    /* card carousel arrows */
    var navBtn = e.target.closest('[data-nav]');
    if (navBtn) {
      e.preventDefault();
      e.stopPropagation();
      var ctrack = navBtn.closest('.lc-card__media').querySelector('[data-track]');
      var dir = parseInt(navBtn.dataset.nav, 10);
      var w = ctrack.clientWidth;
      var ci = Math.round(ctrack.scrollLeft / w) + dir;
      var cmax = ctrack.children.length - 1;
      if (ci < 0) ci = cmax; else if (ci > cmax) ci = 0;
      ctrack.scrollTo({ left: ci * w, behavior: 'smooth' });
      return;
    }

    /* card quick view button: the product page itself, in a modal */
    var qv = e.target.closest('[data-lc-qv]');
    if (qv) {
      e.preventDefault();
      e.stopPropagation();
      openPageModal(qv.dataset.fullUrl || qv.dataset.url);
      return;
    }

    /* card swatch */
    var sw = e.target.closest('[data-swatch]');
    if (sw) {
      e.preventDefault();
      pickSwatch(sw);
      return;
    }

    /* card buy button */
    var act = e.target.closest('[data-card-action]');
    if (act) {
      e.preventDefault();
      var card = act.closest('lc-product-card');
      var action = act.dataset.cardAction;
      if (action === 'qv') {
        openQuickView(act.dataset.url, act.dataset.fullUrl, cardMetal(card));
      } else if (action === 'stack') {
        openStack(card);
      } else {
        var v = cardVariant(card);
        if (!v || v.av === false) return;
        var label = act.textContent;
        act.disabled = true;
        act.textContent = 'Adding…';
        cartAdd([v.id], function () {
          act.disabled = false;
          act.textContent = label;
        }, function () {
          act.disabled = false;
          act.textContent = 'Try again';
        });
      }
      return;
    }

    if (!modal || !modal.classList.contains('on')) return;

    /* quick view option pill */
    var pill = e.target.closest('[data-qv-opt]');
    if (pill && state && state.kind === 'qv') {
      var k = parseInt(pill.dataset.qvOpt, 10);
      state.selections[k] = pill.dataset.value;
      var row = pill.closest('.lc-qv__pills');
      row.querySelectorAll('.lc-qv__pill').forEach(function (b) { b.classList.remove('active'); });
      pill.classList.add('active');
      var picked = modal.querySelector('[data-qv-picked="' + k + '"]');
      if (picked) picked.textContent = pill.dataset.value;
      syncBuyArea();
      return;
    }

    /* quick view gallery arrows */
    var nav = e.target.closest('[data-qv-nav]');
    if (nav) {
      var track = modal.querySelector('[data-qv-track]');
      if (!track) return;
      var w2 = track.clientWidth;
      var dir2 = parseInt(nav.dataset.qvNav, 10);
      var qi = Math.round(track.scrollLeft / w2) + dir2;
      var qmax = track.children.length - 1;
      if (qi < 0) qi = qmax; else if (qi > qmax) qi = 0;
      track.scrollTo({ left: qi * w2, behavior: 'smooth' });
      return;
    }

    /* quick view ATC */
    var atc = e.target.closest('[data-qv-atc]');
    if (atc && state && state.kind === 'qv' && state.variant) {
      atc.disabled = true;
      atc.textContent = 'Adding…';
      cartAdd([state.variant.id], close, function () {
        atc.disabled = false;
        atc.textContent = 'Try again';
      });
      return;
    }

    /* stack ATC */
    var satc = e.target.closest('[data-stack-atc]');
    if (satc && state && state.kind === 'stack') {
      var tier = state.tiers[state.picked];
      if (!tier || tier.available === false) return;
      satc.disabled = true;
      satc.textContent = 'Adding…';
      cartAdd(tier.vids, close, function () {
        satc.disabled = false;
        satc.textContent = 'Try again';
      });
    }
  });

  /* stack tier changes */
  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-stack-tier]') && state && state.kind === 'stack') {
      syncStackTier();
    }
  });

  /* ---------- shop-the-look drawer ---------- */

  var look = null; /* built lazily, shared across tiles */

  function money2(cents) { return '$' + (cents / 100).toFixed(2); }

  function buildLook() {
    if (look) return look;
    look = document.createElement('div');
    look.className = 'lc-look';
    look.setAttribute('aria-hidden', 'true');
    look.innerHTML =
      '<div class="lc-look__wash" data-look-close></div>' +
      '<div class="lc-look__panel" role="dialog" aria-modal="true" aria-label="Shop the look">' +
        '<div class="lc-look__head">' +
          '<h3>Shop The Look</h3>' +
          '<button type="button" class="lc-look__x" data-look-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="lc-look__body" data-look-body></div>' +
      '</div>';
    document.body.appendChild(look);
    look.addEventListener('click', function (e) {
      if (e.target.closest('[data-look-close]')) closeLook();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && look.classList.contains('on')) closeLook();
    });
    return look;
  }

  function openLook(tile) {
    var dataEl = tile.querySelector('[data-look-json]');
    if (!dataEl) return;
    var items;
    try { items = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!items.length) return;

    buildLook();
    var body = look.querySelector('[data-look-body]');
    body.innerHTML = items.map(function (p, i) {
      return '<div class="lc-look__row">' +
        '<img src="' + esc(p.img) + '" alt="" loading="lazy">' +
        '<div>' +
          '<h4>' + esc(p.title) + '</h4>' +
          '<div class="lc-look__price">' + money2(p.price) + '</div>' +
          '<div class="lc-look__btns">' +
            (p.available
              ? '<button type="button" class="lc-look__add" data-look-add data-variant="' + esc(p.variant) + '">Add to Bag</button>'
              : '<button type="button" class="lc-look__add" disabled>Sold Out</button>') +
            '<a class="lc-look__view" href="' + esc(p.url) + '">View</a>' +
          '</div>' +
        '</div>' +
      '</div>';
    }).join('');

    look.classList.add('on');
    look.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lc-qv-lock');
    look.querySelector('.lc-look__x').focus();
  }

  function closeLook() {
    if (!look) return;
    look.classList.remove('on');
    look.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lc-qv-lock');
  }

  document.addEventListener('click', function (e) {
    var tile = e.target.closest('[data-look-tile]');
    if (tile) {
      e.preventDefault();
      openLook(tile);
      return;
    }
    var add = e.target.closest('[data-look-add]');
    if (add) {
      add.disabled = true;
      add.textContent = 'Adding\u2026';
      /* Close our drawer on success: UpCart intercepts /cart/add.js and slides
         its own drawer in over the same edge. */
      cartAdd([add.dataset.variant], closeLook, function () {
        add.disabled = false;
        add.textContent = 'Try again';
      });
    }
  });

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('[data-look-tile]')) {
      e.preventDefault();
      openLook(e.target);
    }
  });
})();
